import React, { createContext, useContext, useEffect, useState } from 'react';

const PWAContext = createContext();

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

export const PWAProvider = ({ children }) => {
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [pushSubscription, setPushSubscription] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    // Check initial notification permission
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }

    // Check online status
    setIsOnline(navigator.onLine);

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      return 'unsupported';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'error';
    }
  };

  const subscribeToPushNotifications = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      //console.log('Push notifications not supported');
      return null;
    }

    try {
      // Request notification permission first
      const permission = await requestNotificationPermission();
      if (permission !== 'granted') {
        return null;
      }

      const registration = await navigator.serviceWorker.ready;
      
      // For now, we'll create a basic subscription
      // In production, you'd use your VAPID public key
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY || null
      });

      setPushSubscription(subscription);
      setIsSubscribed(true);
      
      //console.log('Push notification subscription successful');
      return subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return null;
    }
  };

  const unsubscribeFromPushNotifications = async () => {
    if (!pushSubscription) return;

    try {
      await pushSubscription.unsubscribe();
      setPushSubscription(null);
      setIsSubscribed(false);
      //console.log('Unsubscribed from push notifications');
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
    }
  };

  const showNotification = (title, options = {}) => {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    const notificationOptions = {
      icon: '/naturepulselogo.png',
      badge: '/pi.png',
      vibrate: [100, 50, 100],
      ...options
    };

    new Notification(title, notificationOptions);
  };

  const syncPendingData = async () => {
    if (!('serviceWorker' in navigator)) return;

    try {
      const registration = await navigator.serviceWorker.ready;
      if (registration.sync) {
        await registration.sync.register('sync-posts');
        //console.log('Background sync registered');
      }
    } catch (error) {
      console.error('Error registering background sync:', error);
    }
  };

  const value = {
    notificationPermission,
    isSubscribed,
    pushSubscription,
    isOnline,
    requestNotificationPermission,
    subscribeToPushNotifications,
    unsubscribeFromPushNotifications,
    showNotification,
    syncPendingData
  };

  return (
    <PWAContext.Provider value={value}>
      {children}
    </PWAContext.Provider>
  );
};

export default PWAContext;